/**
 * Derive the webapp console URL from an API URL.
 *
 * The schedule-edit deep-link footer (ENG-4462) needs `AGT_CONSOLE_URL` in
 * the manager's env. Rather than require every operator to export it by
 * hand, we derive it from `AGT_HOST` wherever possible:
 *
 *   https://api.augmented.team       → https://app.augmented.team
 *   http://api.agt.localhost:1355    → http://console.agt.localhost:1355
 *   https://api.<rest>               → https://app.<rest>   (generic fallback)
 *   anything else                    → null
 *
 * Called from two places:
 *   - `agt setup` — persists the derived value to the shell profile / system
 *     env files alongside AGT_HOST / AGT_API_KEY so fresh hosts get the
 *     footer without any extra operator action.
 *   - Manager runtime — fallback when AGT_CONSOLE_URL isn't set, so existing
 *     hosts get the footer on their next delivery tick.
 *
 * Returns null when the host shape doesn't match a known mapping. Callers
 * should log a one-time warning and expect the operator to set
 * AGT_CONSOLE_URL manually in that case.
 */
export function deriveConsoleUrl(apiUrl: string | undefined | null): string | null {
  const trimmed = apiUrl?.trim();
  if (!trimmed) return null;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }

  const host = parsed.hostname;

  // Local-dev portless proxy: api.agt.localhost → console.agt.localhost
  // (The webapp lives under a different label than the `app.` convention
  // used in prod — console is its canonical dev subdomain.)
  if (host === 'api.agt.localhost') {
    parsed.hostname = 'console.agt.localhost';
    return stripTrailingSlash(parsed.toString());
  }

  // Generic api.<rest> → app.<rest>. Covers prod (api.augmented.team →
  // app.augmented.team) and any future per-stage hosts that follow the
  // same convention.
  if (host.startsWith('api.')) {
    parsed.hostname = `app.${host.slice(4)}`;
    return stripTrailingSlash(parsed.toString());
  }

  return null;
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}
